import EnvService from "../env";

/**
 * Utility class for handling encryption and decryption operations
 */
export class EncryptionUtils {
    private static readonly ALGORITHM = 'AES-GCM';
    private static readonly KEY_LENGTH = 256;
    private static readonly IV_LENGTH = 12;
    private static readonly SALT_LENGTH = 16;
    private static readonly ITERATIONS = 100000;
    private static readonly HASH = 'SHA-256';
    private static readonly SECRET_KEY = EnvService.EncryptionSecretKey;

    /**
     * Generates a cryptographic key from a password
     * @param password The password to derive the key from
     * @param salt The salt to use for key derivation
     */
    private static async deriveKey(password: string, salt: Uint8Array<ArrayBuffer>): Promise<CryptoKey> {
        const encoder = new TextEncoder();
        const keyMaterial = await window.crypto.subtle.importKey(
            'raw',
            encoder.encode(password),
            'PBKDF2',
            false,
            ['deriveBits', 'deriveKey']
        );

        return window.crypto.subtle.deriveKey(
            {
                name: 'PBKDF2',
                salt: salt,
                iterations: this.ITERATIONS,
                hash: this.HASH
            },
            keyMaterial,
            {name: this.ALGORITHM, length: this.KEY_LENGTH},
            false,
            ['encrypt', 'decrypt']
        );
    }

    /**
     * Encrypts data using AES-GCM
     * @param data The data to encrypt
     * @param password The password to use for encryption. If not provided, the default secret key will be used.
     */
    static async encrypt(data: string, password: string = this.SECRET_KEY): Promise<string> {
        try {
            // Generate random salt and IV
            const salt = window.crypto.getRandomValues(new Uint8Array(this.SALT_LENGTH));
            const iv = window.crypto.getRandomValues(new Uint8Array(this.IV_LENGTH));

            // Derive key from password
            const key = await this.deriveKey(password, salt);

            // Encrypt the data
            const encoder = new TextEncoder();
            const encryptedContent = await window.crypto.subtle.encrypt(
                {
                    name: this.ALGORITHM,
                    iv: iv
                },
                key,
                encoder.encode(data)
            );

            // Combine salt, IV, and encrypted data
            const encryptedArray = new Uint8Array(encryptedContent);
            const combined = new Uint8Array(salt.length + iv.length + encryptedArray.length);
            combined.set(salt);
            combined.set(iv, salt.length);
            combined.set(encryptedArray, salt.length + iv.length);

            // Convert to base64 for storage
            return btoa(String.fromCharCode(...combined));
        } catch (error) {
            console.error('Encryption failed:', error);
            throw error;
        }
    }

    /**
     * Decrypts data that was encrypted using AES-GCM
     * @param encryptedData The encrypted data to decrypt
     * @param password The password to use for decryption. If not provided, the default secret key will be used.
     */
    static async decrypt(encryptedData: string, password: string = this.SECRET_KEY): Promise<string> {
        try {
            // Convert from base64
            const binaryString = atob(encryptedData);
            const combined = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
                combined[i] = binaryString.charCodeAt(i);
            }

            // Extract salt, IV, and encrypted data
            const salt = combined.slice(0, this.SALT_LENGTH);
            const iv = combined.slice(this.SALT_LENGTH, this.SALT_LENGTH + this.IV_LENGTH);
            const encryptedArray = combined.slice(this.SALT_LENGTH + this.IV_LENGTH);

            // Derive key from password
            const key = await this.deriveKey(password, salt);

            // Decrypt the data
            const decryptedContent = await window.crypto.subtle.decrypt(
                {
                    name: this.ALGORITHM,
                    iv: iv
                },
                key,
                encryptedArray
            );

            // Convert decrypted data to string
            const decoder = new TextDecoder();
            return decoder.decode(decryptedContent);
        } catch (error) {
            console.error('Decryption failed:', error);
            throw error;
        }
    }
} 